import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { CheckCircle2, MessageSquare, Zap, Clock, TrendingUp, Users } from "lucide-react";
import { BookingModal } from "@/components/BookingModal";

const features = [
  {
    title: "Immediate Text Response",
    description: "Automatically send a customized SMS to callers the second you miss their call, letting them know you care.",
    icon: <MessageSquare className="h-6 w-6" />,
  },
  {
    title: "Lightning Fast Setup",
    description: "Get up and running in minutes. Our system integrates seamlessly with your existing phone lines.",
    icon: <Zap className="h-6 w-6" />,
  },
  {
    title: "24/7 Availability",
    description: "Even when your office is closed, Missed Call Rescue works around the clock to capture leads.",
    icon: <Clock className="h-6 w-6" />,
  },
  {
    title: "Increase Revenue",
    description: "Stop leaving money on the table. Every rescued call is a potential sale saved from a competitor.",
    icon: <TrendingUp className="h-6 w-6" />,
  },
  {
    title: "Better Customer Experience",
    description: "Clients appreciate immediate acknowledgment. Build trust before you even speak with them.",
    icon: <Users className="h-6 w-6" />,
  },
  {
    title: "Customizable Messages",
    description: "Tailor your auto-responses to match your brand voice and specific business needs.",
    icon: <CheckCircle2 className="h-6 w-6" />,
  },
];

const Features = () => {
  return (
    <Layout>
      <SEO
        title="Features | Automation & AI for Small Businesses in Edmonton"
        description="Discover the powerful features of Missed Call Rescue. We provide automated return phone call systems and AI solutions for small businesses in Edmonton."
        canonical="/features"
      />
      {/* Page Header */}
      <section className="bg-primary pt-20 pb-16 text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">AI Automation Features for Edmonton Small Businesses</h1>
          <p className="text-xl text-primary-foreground/80 max-w-2xl mx-auto">
            Our automated return phone call systems and intelligent AI solutions ensure no client in Edmonton—or anywhere else—slips through the cracks.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">How It Works</h2>
            <div className="w-24 h-1 bg-secondary mx-auto rounded-full"></div>
          </div>

          <div className="flex flex-col md:flex-row justify-center items-center gap-8 md:gap-12 relative">
            {/* Step 1 */}
            <div className="flex-1 text-center relative z-10">
              <div className="w-20 h-20 mx-auto bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6 shadow-lg border-4 border-white">
                1
              </div>
              <h3 className="text-xl font-bold mb-2">You Miss a Call</h3>
              <p className="text-slate-600">You're busy helping another client, in a meeting, or it's after hours.</p>
            </div>

            {/* Connector */}
            <div className="hidden md:block absolute top-10 left-1/6 right-1/6 h-0.5 bg-slate-200 -z-0"></div>

            {/* Step 2 */}
            <div className="flex-1 text-center relative z-10">
              <div className="w-20 h-20 mx-auto bg-secondary text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6 shadow-lg border-4 border-white">
                2
              </div>
              <h3 className="text-xl font-bold mb-2">Auto-Text Sent</h3>
              <p className="text-slate-600">Our system instantly texts the caller: "Sorry we missed you! How can we help?"</p>
            </div>

            {/* Step 3 */}
            <div className="flex-1 text-center relative z-10">
              <div className="w-20 h-20 mx-auto bg-accent text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6 shadow-lg border-4 border-white">
                3
              </div>
              <h3 className="text-xl font-bold mb-2">Lead Captured</h3>
              <p className="text-slate-600">The caller replies via text, keeping them engaged and preventing them from calling a competitor.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Grid */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Why Choose Missed Call Rescue?</h2>
            <div className="w-24 h-1 bg-secondary mx-auto rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-primary/5 text-primary rounded-lg flex items-center justify-center mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-white text-center">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold mb-6">Stop losing business to missed calls</h2>
          <p className="text-lg text-slate-600 mb-8">
            Setup takes less than 10 minutes. Start rescuing your missed calls today.
          </p>
          <BookingModal>
            <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 cursor-pointer">
              Get Started Now
            </Button>
          </BookingModal>
        </div>
      </section>
    </Layout>
  );
};

export default Features;
